import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  FileText, CheckCircle2, Download, ArrowRight, 
  Clock, AlertCircle, Loader2, RotateCw, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function CustomerEstimates() {
  const navigate = useNavigate();
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [feedbackMsg, setFeedbackMsg] = useState('');

  useEffect(() => {
    fetchEstimates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEstimates = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('customer_token');
      if (!token) {
        navigate('/customer-login');
        return;
      }
      const res = await fetch(`${API}/api/client-portal/estimates`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error('Failed to fetch estimates');
      }
      const data = await res.json();
      setEstimates(data.items || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptEstimate = async (id, num) => {
    if (!window.confirm(`Are you sure you want to approve & accept Estimate #${num}? Our operations desk will begin booking fulfillment.`)) {
      return;
    }

    setActionLoading(id);
    setFeedbackMsg('');
    try {
      const token = localStorage.getItem('customer_token');
      const res = await fetch(`${API}/api/client-portal/estimates/${id}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to accept estimate');
      }
      setFeedbackMsg(data.message || 'Estimate accepted successfully!');
      fetchEstimates();
    } catch (err) {
      alert(err.message || 'Error accepting estimate');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownloadPdf = (id) => {
    window.open(`/estimates/print/${id}`, '_blank');
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-manrope text-slate-900">Quotations &amp; Commercial Estimates</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Review freight quotations prepared for you and approve shipments directly online
            </p>
          </div>
          <Button
            variant="outline"
            onClick={fetchEstimates}
            className="text-xs font-bold border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl h-10 px-4"
          >
            <RotateCw className="w-3.5 h-3.5 mr-1.5" />
            Refresh
          </Button>
        </div>

        {feedbackMsg && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl p-4 text-xs flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{feedbackMsg}</span>
            </div>
            <button
              onClick={() => setFeedbackMsg('')}
              className="text-emerald-700 font-bold hover:underline"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Estimates Table */}
        <Card className="bg-white rounded-xl border border-slate-200/80 overflow-hidden shadow-xs">
          {loading ? (
            <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="text-xs">Loading commercial quotations...</span>
            </div>
          ) : estimates.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center p-6 text-slate-400">
              <FileText className="w-12 h-12 text-slate-300 mb-3 stroke-1" />
              <p className="text-base font-bold text-slate-800 font-manrope">No Quotations Available</p>
              <p className="text-xs text-slate-400 mt-1 max-w-sm">
                Any official freight estimates or quotations shared with your company will be listed here.
              </p>
              <Button
                onClick={() => navigate('/customer-portal/request-booking')}
                className="mt-4 bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs h-9 px-4 rounded-xl"
              >
                Request a Freight Quote
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-semibold text-[11px]">
                  <tr>
                    <th className="px-5 py-3.5">Estimate No</th>
                    <th className="px-5 py-3.5">Date</th>
                    <th className="px-5 py-3.5">Scope &amp; Route</th>
                    <th className="px-5 py-3.5">Grand Total</th>
                    <th className="px-5 py-3.5">Valid Until</th>
                    <th className="px-5 py-3.5">Status</th>
                    <th className="px-5 py-3.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {estimates.map((est) => {
                    const isAccepted = est.status === 'ACCEPTED';
                    return (
                      <tr key={est.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-5 py-3.5 font-bold font-mono text-slate-900">
                          {est.estimate_number}
                        </td>
                        <td className="px-5 py-3.5 text-slate-600 whitespace-nowrap">
                          {est.date || '-'}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="font-bold text-slate-800 block">{est.shipment_scope}</span>
                          <span className="text-[10px] text-slate-400 block">{est.origin} ➔ {est.destination}</span>
                        </td>
                        <td className="px-5 py-3.5 font-bold text-slate-900 font-manrope text-sm whitespace-nowrap">
                          ₹{est.grand_total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-5 py-3.5 text-slate-500 whitespace-nowrap">
                          {est.valid_until || 'Upon request'}
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-block px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                            isAccepted
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`}>
                            {est.status}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-right space-x-1 whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownloadPdf(est.id)}
                            className="h-8 text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2.5 rounded-lg"
                          >
                            <Download className="w-3.5 h-3.5 mr-1" />
                            PDF
                          </Button>
                          {!isAccepted && (
                            <Button
                              size="sm"
                              disabled={actionLoading === est.id}
                              onClick={() => handleAcceptEstimate(est.id, est.estimate_number)}
                              className="h-8 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-3 rounded-lg shadow-xs"
                            >
                              {actionLoading === est.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <>
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                  <span>Accept &amp; Book</span>
                                </>
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}
